/** Dependencies only add read access; a read grant never implies a command or funds operation. */
export const businessViewDependencies = {
    'city.manage': 'city.view',
    'app.manage': 'app.view',
    'app.product.manage': 'app.view',
    'product.manage': 'product.view',
    'cms.manage': 'cms.view',
    'cms.publish': 'cms.view',
    'order.confirm': 'order.view',
    'order.fulfillment.manage': 'order.view',
    'order.complete': 'order.view',
    'order.delete': 'order.view',
    'order.export': 'order.view',
    'order.communication.create': 'order.communication.view',
    'order.communication.view': 'order.view',
    'refund.review': 'refund.view',
    'refund.process': 'refund.view',
    'after_sales.manage': 'after_sales.view',
    'after_sales.compensation.record': 'after_sales.view',
    'member.manage': 'member.view',
    'member.events.view': 'member.view',
    'member.tags.view': 'member.view',
    'member.note.manage': 'member.view',
    'member.export': 'member.view',
    'member.tags.manage': 'member.tags.view',
    'marketing.coupon.manage': 'marketing.coupon.view',
    'marketing.rescue.manage': 'marketing.rescue.view',
    'channel.manage': 'channel.view',
    'channel.promoter.manage': 'channel.promoter.view',
    'channel.promoter.view': 'channel.view',
    'channel.link.manage': 'channel.link.view',
    'channel.link.view': 'channel.view',
    'channel.performance.view': 'channel.view',
    'commission.rule.manage': 'commission.rule.view',
    'commission.export': 'commission.view',
    'settlement.generate': 'settlement.view',
    'settlement.review': 'settlement.view',
    'settlement.execute': 'settlement.view',
    'settlement.manage': 'settlement.view',
    'settlement.confirm': 'settlement.view',
    'payment.manage': 'payment.view',
    'payment.refund': 'payment.view',
    'merchant.change': 'payment.view',
    'finance.reconcile': 'finance.view',
    'finance.rule.manage': 'finance.rule.view',
    'finance.export': 'finance.view',
    'report.export': 'report.view',
    'message.manage': 'message.view',
    'message.send': 'message.view',
    'system.security.manage': 'system.security.view',
    'release.manage': 'release.view',
    'release.publish': 'release.view',
    'ops.monitor.manage': 'ops.monitor.view',
    'ops.recovery.manage': 'ops.recovery.view',
};
export function requiredBusinessViewPermissions(code) {
    const result = [], seen = new Set([code]);
    let required = businessViewDependencies[code];
    while (required && !seen.has(required)) {
        seen.add(required);
        result.push(required);
        required = businessViewDependencies[required];
    }
    return result;
}
export class BusinessViewDependencyError extends Error {
    permissionCode;
    requiredPermissionCode;
    conflict;
    constructor(permissionCode, requiredPermissionCode, conflict) {
        super(conflict === 'DENIED'
            ? '所选操作需要对应的查看权限，但该查看权限已明确禁止；请先调整禁止，或取消所选操作。'
            : '所选操作需要对应的查看权限，但该查看权限尚未开通或已停用，暂不能授权。');
        this.permissionCode = permissionCode;
        this.requiredPermissionCode = requiredPermissionCode;
        this.conflict = conflict;
        this.name = 'BusinessViewDependencyError';
    }
}
export function normalizeBusinessViewPermissions(rules, permissions) {
    const next = rules.map((rule) => ({ ...rule })), addedViewPermissions = [];
    const active = new Map(permissions
        .filter((permission) => permission.status === 'ACTIVE')
        .map((permission) => [permission.code, permission]));
    const byId = new Map(permissions.map((permission) => [permission.id, permission]));
    for (const rule of rules) {
        if (rule.effect !== 'ALLOW')
            continue;
        const selected = byId.get(rule.permissionId);
        if (!selected)
            continue;
        for (const code of requiredBusinessViewPermissions(selected.code)) {
            const prerequisite = active.get(code);
            if (!prerequisite)
                throw new BusinessViewDependencyError(selected.code, code, 'UNAVAILABLE');
            const existing = next.find((item) => item.permissionId === prerequisite.id);
            if (existing?.effect === 'DENY')
                throw new BusinessViewDependencyError(selected.code, code, 'DENIED');
            if (!existing) {
                next.push({ permissionId: prerequisite.id, effect: 'ALLOW' });
                addedViewPermissions.push(code);
            }
        }
    }
    return { rules: next, addedViewPermissions };
}
/** Limited aliases from historical editors; defaults must not bypass an existing alias restriction. */
export function canonicalBusinessFieldName(fieldName) {
    const aliases = {
        contactWechat: 'wechatNo',
        'contact.contactName': 'contactName',
        'contact.mobile': 'mobile',
        'contact.wechatNo': 'wechatNo',
        'tripInfo.contactName': 'contactName',
        'tripInfo.mobile': 'mobile',
        'tripInfo.wechatNo': 'wechatNo',
        'tripInfo.pickupAddress': 'pickupAddress',
        'tripInfo.startTime': 'startTime',
        'tripInfo.remark': 'remark',
    };
    return aliases[fieldName] ?? fieldName;
}
const commonOrderFields = [
    ['contactName', 'L1'],
    ['mobile', 'L1'],
    ['wechatNo', 'L1'],
    ['grossAmountCents', 'L2'],
    ['discountAmountCents', 'L2'],
    ['payAmountCents', 'L2'],
    ['refundAmountCents', 'L2'],
    ['actualPaidAmountCents', 'L2'],
    ['currency', 'L2'],
];
const commonFields = {
    'order.view': commonOrderFields,
    'payment.view': [
        ...commonOrderFields,
        ['amountCents', 'L2'],
        ['outTradeNo', 'L2'],
        ['paymentMchid', 'L2'],
    ],
    'refund.view': [...commonOrderFields, ['amountCents', 'L2'], ['originalPaymentMchid', 'L2']],
};
/** Called for newly selected functions/presets only; does not change defaults on historical roles. */
export function getCommonReadFieldPolicies(permissionCodes) {
    const codes = new Set(permissionCodes.flatMap((code) => [code, ...requiredBusinessViewPermissions(code)]));
    return [...codes].flatMap((code) => (commonFields[code] ?? []).map(([fieldName, classification]) => ({
        code,
        fieldName,
        classification,
        access: 'ALLOW',
    })));
}
export function ensureCommonReadFieldPolicies(existingRules, permissionCodes, permissions) {
    const rules = existingRules.map((rule) => ({ ...rule })), addedFields = [];
    const active = new Map(permissions
        .filter((permission) => permission.status === 'ACTIVE')
        .map((permission) => [permission.code, permission]));
    for (const field of getCommonReadFieldPolicies(permissionCodes)) {
        const permission = active.get(field.code);
        if (!permission ||
            rules.some((rule) => rule.permissionId === permission.id &&
                canonicalBusinessFieldName(rule.fieldName) === field.fieldName))
            continue;
        rules.push({
            permissionId: permission.id,
            fieldName: field.fieldName,
            classification: field.classification,
            access: field.access,
        });
        addedFields.push(field);
    }
    return { rules, addedFields };
}
//# sourceMappingURL=business-permission-presets.js.map